'use client'

import { useEffect } from 'react'
import { Tenancy } from '@/types/client'
import { CalendarCheck, AlertCircle, CheckCircle } from 'lucide-react'
import { useCurrency } from '@/lib/currency'

interface MyTenanciesProps {
  tenancies: Tenancy[]
  loading: boolean
  onRefresh: () => void
  onPayNextPeriod?: (applicationId: string, propertyTitle: string, amount: number, currency?: string) => void
}

function formatDate(dateStr: string) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDuration(months: number | undefined): string {
  if (months == null || months < 1) return '—'
  if (months >= 12) {
    const years = Math.floor(months / 12)
    const remainder = months % 12
    if (remainder === 0) return `${years} year${years !== 1 ? 's' : ''}`
    return `${years} year${years !== 1 ? 's' : ''} ${remainder} month${remainder !== 1 ? 's' : ''}`
  }
  return `${months} month${months !== 1 ? 's' : ''}`
}

export default function MyTenancies({ tenancies, loading, onRefresh, onPayNextPeriod }: MyTenanciesProps) {
  const { format } = useCurrency()

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="relative inline-block">
          <div className="w-12 h-12 border-4 border-emerald-200 rounded-full" />
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0" />
        </div>
        <p className="ml-4 text-stone-600 font-medium">Loading your tenancies...</p>
      </div>
    )
  }

  if (tenancies.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
        <CalendarCheck className="w-16 h-16 mx-auto text-stone-300 mb-4" />
        <h3 className="text-xl font-display text-stone-900 mb-2">No Active Tenancies</h3>
        <p className="text-stone-600 max-w-md mx-auto">
          When you pay rent for a property, your rent period and expiration date will appear here so you can see when to renew or pay for the next period.
        </p>
      </div>
    )
  }

  const expiringSoon = tenancies.filter(t => t.status === 'expiring_soon')
  const expired = tenancies.filter(t => t.status === 'expired')
  const active = tenancies.filter(t => t.status === 'active')

  return (
    <div className="space-y-6">
      {(expiringSoon.length > 0 || expired.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">
              {expired.length > 0
                ? `Rent expired for ${expired.length} tenancy(ies)`
                : `${expiringSoon.length} tenancy(ies) expiring in the next 30 days`}
            </p>
            <p className="text-sm text-amber-800 mt-1">
              Pay for the next period to renew your tenancy, or contact your landlord to discuss.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {[...expired, ...expiringSoon, ...active].map((tenancy) => {
          const prop = tenancy.property
          const isExpired = tenancy.status === 'expired'
          const isExpiringSoon = tenancy.status === 'expiring_soon'
          const canRenew = (isExpired || isExpiringSoon) && onPayNextPeriod && (tenancy.monthlyRent ?? tenancy.lastPaymentAmount) != null

          return (
            <div
              key={tenancy.applicationId}
              className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-display text-stone-900 truncate">{prop?.title || 'Property'}</h3>
                  <div className="mt-3 flex flex-wrap gap-3 text-sm text-stone-600">
                    <span>
                      Rent period: {formatDate(tenancy.rentPeriodStart)} → {formatDate(tenancy.rentPeriodEnd)}
                    </span>
                    {tenancy.leaseDurationMonths != null && (
                      <span>Duration: {formatDuration(tenancy.leaseDurationMonths)}</span>
                    )}
                    {(tenancy.monthlyRent != null || tenancy.lastPaymentAmount != null) && (
                      <span>
                        {format((tenancy.monthlyRent ?? tenancy.lastPaymentAmount) ?? 0)}
                        {(tenancy.leaseDurationMonths ?? 12) >= 12 ? '/year' : '/mo'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      isExpired
                        ? 'bg-red-100 text-red-800'
                        : isExpiringSoon
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {isExpired ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : isExpiringSoon ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {isExpired
                      ? `Expired ${Math.abs(tenancy.daysUntilExpiry)} days ago`
                      : isExpiringSoon
                        ? `Expires in ${tenancy.daysUntilExpiry} days`
                        : `Active · ${tenancy.daysUntilExpiry} days left`}
                  </span>
                  <p className="text-xs text-stone-500">
                    Rent paid until: <strong>{formatDate(tenancy.rentExpirationDate)}</strong>
                  </p>
                  {canRenew && (
                    <button
                      type="button"
                      onClick={() => onPayNextPeriod?.(
                        tenancy.applicationId,
                        prop?.title || 'Property',
                        tenancy.monthlyRent ?? tenancy.lastPaymentAmount ?? 0,
                        tenancy.currency
                      )}
                      className="mt-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      Pay next period / Renew
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
